import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("hs_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // Slide the session on load (short-lived refresh) and honour "sign out everywhere".
    api
      .post("/auth/refresh")
      .then((r) => { localStorage.setItem("hs_token", r.data.token); setUser(r.data.user); })
      .catch((e) => { const s = e.response?.status; if (s === 401 || s === 403) localStorage.removeItem("hs_token"); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required || data.mfa_setup_required) return { ok: false, mfa: data };
      localStorage.setItem("hs_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user, mfaSuggest: !!data.mfa_suggest };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        return { ok: false, needsVerification: true, email: detail.email || email, error: detail.message };
      }
      return { ok: false, error: formatApiErrorDetail(detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.verification_required) return { ok: true, verificationRequired: true, email: data.email };
      localStorage.setItem("hs_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogleSession = async (sessionId, link = false) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId, link });
      localStorage.setItem("hs_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user, needs_role: data.needs_role, linked: data.linked };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const adoptToken = async (token, needsRole) => {
    try {
      localStorage.setItem("hs_token", token);
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { ok: true, user: data, needs_role: needsRole ?? !!data.needs_role };
    } catch (e) {
      localStorage.removeItem("hs_token");
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const selectRole = async (role) => {
    try {
      const { data } = await api.post("/auth/select-role", { role });
      localStorage.setItem("hs_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      localStorage.setItem("hs_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = detail && typeof detail === "object" ? detail.message : formatApiErrorDetail(detail);
      return { ok: false, error: msg || e.message };
    }
  };

  const resendVerification = async (email) => {
    try { await api.post("/auth/resend-verification", { email }); } catch { /* generic — never leak */ }
    return { ok: true };
  };

  const logoutEverywhere = async () => {
    try {
      const { data } = await api.post("/auth/logout-all");
      localStorage.setItem("hs_token", data.token);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const adoptSession = (data) => { localStorage.setItem("hs_token", data.token); setUser(data.user); };

  const verifyMfa = async (mfa_token, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token, code });
      adoptSession(data);
      return { ok: true, user: data.user, usedBackup: data.used_backup_code, backupRemaining: data.backup_remaining };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("hs_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, logoutEverywhere, loginWithGoogleSession, adoptToken, selectRole, verifyEmail, resendVerification, verifyMfa, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
